import React, { useState, useEffect } from 'react';
import axios from 'axios';
import constant from '../constants/constant';
import { Target, Plus, Edit2, Trash2, Save, X, RefreshCw } from 'lucide-react';

const TargetsConfiguration = () => {
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [toast, setToast] = useState({ message: '', type: '', visible: false });
  const showToast = (message, type = 'success') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast({ message: '', type: '', visible: false }), 3000);
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [formData, setFormData] = useState({
    level_number: '',
    target_coins: '',
    reward_diamonds: '',
    room_bonus_pct: 20,
    exchange_bonus_pct: 10,
    agency_comm_pct: 18,
    bd_comm_pct: 22,
    manager_comm_pct: 15
  });

  useEffect(() => {
    fetchLevels();
  }, []);

  const fetchLevels = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${constant.API_BASE_URL}/admin/targets/levels`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) setLevels(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (level = null) => {
    if (level) {
      setEditingLevel(level);
      setFormData({ ...level });
    } else {
      setEditingLevel(null);
      setFormData({
        level_number: levels.length + 1,
        target_coins: '',
        reward_diamonds: '',
        room_bonus_pct: 20,
        exchange_bonus_pct: 10,
        agency_comm_pct: 18,
        bd_comm_pct: 22,
        manager_comm_pct: 15
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${constant.API_BASE_URL}/admin/targets/levels`, formData, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      setIsModalOpen(false);
      showToast(editingLevel ? 'Level updated successfully' : 'Level created successfully');
      fetchLevels();
    } catch (err) {
      showToast("Error saving level: " + err.message, 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this level?")) {
      try {
        await axios.delete(`${constant.API_BASE_URL}/admin/targets/levels/${id}`, {
          headers: { Authorization: sessionStorage.getItem('adminToken') }
        });
        showToast('Level deleted successfully');
        fetchLevels();
      } catch (err) {
        showToast("Error deleting level", 'error');
      }
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">Target Configuration</h2>
          <p className="text-slate-500 mt-1">Manage target levels, coin targets, diamond rewards, and commission percentages.</p>
        </div>
        <div className="flex gap-3">
          <button onClick={fetchLevels} className="flex items-center gap-2 bg-white text-slate-700 px-5 py-2.5 rounded-xl font-semibold shadow-sm border border-gray-200 hover:bg-gray-50 transition">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button onClick={() => handleOpenModal()} className="flex items-center gap-2 bg-pink-600 text-white px-5 py-2.5 rounded-xl font-semibold shadow-lg shadow-pink-500/30 hover:bg-pink-700 transition">
            <Plus size={18} /> Add New Level
          </button>
        </div>
      </div>

      <div className="bg-white rounded-[24px] shadow-xl shadow-slate-100/30 border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-20 text-center text-slate-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                  <th className="p-5">Level</th>
                  <th className="p-5">Coin Target</th>
                  <th className="p-5">Total Reward (💎)</th>
                  <th className="p-5 text-emerald-600">70% Basic (Immediate 💎)</th>
                  <th className="p-5 text-amber-600">30% Hold (10 Days 💎)</th>
                  <th className="p-5">Comm (Ag/BD/Mg)</th>
                  <th className="p-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {levels.map(level => {
                  const reward = parseInt(level.reward_diamonds) || 0;
                  const basic70 = Math.floor(reward * 0.70);
                  const hold30 = reward - basic70;

                  return (
                    <tr key={level.id} className="hover:bg-slate-50/50 transition">
                      <td className="p-5 font-bold text-slate-800">Level {level.level_number}</td>
                      <td className="p-5 font-extrabold text-slate-700">{parseInt(level.target_coins).toLocaleString()}</td>
                      <td className="p-5 font-extrabold text-pink-600">💎 {reward.toLocaleString()}</td>
                      <td className="p-5 font-bold text-emerald-600">💎 {basic70.toLocaleString()}</td>
                      <td className="p-5 font-bold text-amber-600">💎 {hold30.toLocaleString()}</td>
                      <td className="p-5 text-slate-500">
                        {level.agency_comm_pct}% / {level.bd_comm_pct}% / {level.manager_comm_pct}%
                      </td>
                      <td className="p-5 text-right flex justify-end gap-2">
                        <button onClick={() => handleOpenModal(level)} className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"><Edit2 size={16} /></button>
                        <button onClick={() => handleDelete(level.id)} className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100"><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  );
                })}
                {levels.length === 0 && (
                  <tr><td colSpan="7" className="p-10 text-center text-slate-500">No target levels defined.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Host Active Stats Admin Override UI Panel */}
      <div className="bg-white rounded-[24px] shadow-xl shadow-slate-100/30 border border-slate-100 p-6 space-y-4">
        <div>
          <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <RefreshCw className="text-pink-600" size={20} /> Host Active Days & Timing Override Panel
          </h3>
          <p className="text-slate-500 text-xs mt-1">Manually update active minutes and valid days for any host for QA testing and operational corrections.</p>
        </div>

        <form onSubmit={async (e) => {
          e.preventDefault();
          const userId = e.target.target_user_id.value;
          const mins = e.target.duration_mins.value;
          const days = e.target.valid_days.value;

          try {
            const res = await axios.post(`${constant.API_BASE_URL}/host/center/admin/override-active-stats`, {
              target_user_id: userId,
              duration_mins: mins,
              valid_days: days
            }, {
              headers: { Authorization: sessionStorage.getItem('adminToken') }
            });
            if (res.data.success) {
              showToast(res.data.message || 'Host active stats updated successfully!');
              e.target.reset();
            } else {
              showToast(res.data.message || 'Failed to update host stats', 'error');
            }
          } catch (err) {
            showToast("Error updating host stats: " + (err.response?.data?.message || err.message), 'error');
          }
        }} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-500 uppercase">Host XOXO ID (Or UUID)</label>
            <input name="target_user_id" type="text" placeholder="e.g. 100234" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" required />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-500 uppercase">Active Mins / Day</label>
            <input name="duration_mins" type="number" defaultValue="120" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" required />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-500 uppercase">Valid Active Days</label>
            <input name="valid_days" type="number" defaultValue="10" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" required />
          </div>
          <button type="submit" className="w-full py-2.5 bg-slate-900 text-white rounded-xl font-bold text-sm hover:bg-slate-800 transition">
            Set Host Stats
          </button>
        </form>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-800">{editingLevel ? 'Edit Level' : 'Add Level'}</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:bg-slate-50 p-1 rounded-lg"><X size={24} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Level Number</label>
                  <input type="number" value={formData.level_number} onChange={e => setFormData({...formData, level_number: e.target.value})} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl" required />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Target Coins</label>
                  <input type="number" value={formData.target_coins} onChange={e => setFormData({...formData, target_coins: e.target.value})} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl" required />
                </div>
                <div className="space-y-1 col-span-2 sm:col-span-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Total Reward Diamonds</label>
                  <input type="number" value={formData.reward_diamonds} onChange={e => setFormData({...formData, reward_diamonds: e.target.value})} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl" required />
                  {formData.reward_diamonds > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1 text-[11px]">
                      <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-lg font-bold">
                        70% Basic: 💎 {Math.floor(Number(formData.reward_diamonds) * 0.70).toLocaleString()}
                      </span>
                      <span className="bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-lg font-bold">
                        30% Hold: 💎 {(Number(formData.reward_diamonds) - Math.floor(Number(formData.reward_diamonds) * 0.70)).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Agency Comm %</label>
                  <input type="number" step="0.01" value={formData.agency_comm_pct} onChange={e => setFormData({...formData, agency_comm_pct: e.target.value})} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl" required />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">BD Comm %</label>
                  <input type="number" step="0.01" value={formData.bd_comm_pct} onChange={e => setFormData({...formData, bd_comm_pct: e.target.value})} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl" required />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Manager Comm %</label>
                  <input type="number" step="0.01" value={formData.manager_comm_pct} onChange={e => setFormData({...formData, manager_comm_pct: e.target.value})} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl" required />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Exchange Bonus %</label>
                  <input type="number" step="0.01" value={formData.exchange_bonus_pct} onChange={e => setFormData({...formData, exchange_bonus_pct: e.target.value})} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl" required />
                </div>
              </div>
              <div className="pt-4 flex justify-end gap-3">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-6 py-2.5 text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition">Cancel</button>
                <button type="submit" className="px-6 py-2.5 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition">Save Level</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast.visible && (
        <div className={`fixed bottom-4 right-4 z-[9999] px-6 py-3 rounded-xl shadow-lg border text-sm font-bold flex items-center gap-2 animate-in slide-in-from-bottom-5 fade-in duration-300 ${toast.type === 'error' ? 'bg-red-50 text-red-600 border-red-100' : 'bg-green-50 text-green-700 border-green-100'}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default TargetsConfiguration;

